import React, { useMemo, useState, useEffect, createContext, Component } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import theme from './theme';
import { CssBaseline } from '@mui/material';
import { BrowserRouter } from 'react-router-dom';
import { AppRouter } from './routing';
import { Provider, useSelector } from 'react-redux';
import { store } from './store';
import { setupTokenAutoRefresh } from './shared/utils/tokenRefreshInterceptor';
import { ToastProvider, useToast } from './shared/components/notifications/ToastProvider';
import SuperAdminBadge from './shared/components/navigation/SuperAdminBadge';
import { initializeGlobalTracking } from './shared/hooks/useAuditTracking';
import { useVersionCheck } from './shared/hooks/useVersionCheck';
import UpdateBanner from './shared/components/notifications/UpdateBanner';

// Create a context for theme mode
export const ThemeContext = createContext();

// Error boundary for VersionChecker to prevent crashes
class VersionCheckerErrorBoundary extends Component {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('VersionChecker error:', error, info);
  }
  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

// Wrapper component that runs inside BrowserRouter + ToastProvider
function VersionChecker() {
  const { success } = useToast();

  // Get current user's portal from Redux for portal-specific release notes
  const user = useSelector((state) => state.auth?.user);
  const currentPortal = user?.assigned_portal || null;

  // Pass portal to hook for filtering release notes
  const { versionInfo, dismiss, updateNow, justUpdated, clearJustUpdated } =
    useVersionCheck(currentPortal);

  // Show success toast after a successful update reload (only once)
  useEffect(() => {
    if (justUpdated) {
      success('Updated to the latest version successfully.');
      clearJustUpdated();
    }
  }, [justUpdated, success, clearJustUpdated]);

  if (!versionInfo) return null;

  return (
    <UpdateBanner
      versionInfo={versionInfo}
      onDismiss={dismiss}
      onUpdateNow={updateNow}
    />
  );
}

function App() {
  // Get initial theme from localStorage or default to 'light'
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || 'light';
  });

  // Save theme preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  // Setup automatic token refresh and global tracking on app start
  useEffect(() => {
    setupTokenAutoRefresh();
    initializeGlobalTracking();
  }, []);

  const toggleMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const muiTheme = useMemo(() => theme(mode), [mode]);

  // Create the theme context value
  const themeContextValue = useMemo(
    () => ({
      mode,
      toggleMode,
    }),
    [mode]
  );

  return (
    <Provider store={store}>
      <ThemeContext.Provider value={themeContextValue}>
        <MuiThemeProvider theme={muiTheme}>
          <CssBaseline />
          <ToastProvider>
            <BrowserRouter>
              <VersionCheckerErrorBoundary>
                <VersionChecker />
              </VersionCheckerErrorBoundary>
              <SuperAdminBadge />
              <AppRouter />
            </BrowserRouter>
          </ToastProvider>
        </MuiThemeProvider>
      </ThemeContext.Provider>
    </Provider>
  );
}

export default App;
